import update from 'immutability-helper';
import { getRoute, getRouter } from './initRouter.js';
import { buildMaybePartnerRouteWithCurrentRoute } from './router.js';

function buildMaybePartnerRoute(location) {
    const currentRoute = getRoute();
    let newLocationObject = location;
    if (typeof location === 'object' && location.query) {
        newLocationObject = {
            ...location,
            query: { ...currentRoute.value.query, ...location.query },
        };
    }
    // Casting to any due to function overloading limitations.
    return buildMaybePartnerRouteWithCurrentRoute(newLocationObject, currentRoute.value);
}
/**
 * This function will replace the path query silently without forcing a reload.
 * This will not invoke the router to update based on these params, but if a
 * reload is forced, the new query will be used.
 * @param queryUpdate query updates
 */
const updateQuerySilently = (queryUpdate) => {
    const currentRoute = getRoute();
    const query = {
        ...currentRoute.value.query,
        ...queryUpdate,
    };
    let { path } = currentRoute.value;
    let paramPrefix = '?';
    Object.entries(query).forEach((queryParam) => {
        const [key, value] = queryParam;
        if (!value || (value === null || value === void 0 ? void 0 : value[0]) === null)
            return;
        path = `${path}${paramPrefix}${key}=${value}`;
        if (paramPrefix === '?')
            paramPrefix = '&';
    });
    window.history.pushState({}, '', path);
};
/**
 * Given a new location, merge in the current query values.
 */
const keepQuery = (location) => {
    const route = getRoute();
    const { query } = route.value;
    return { ...location, query: { ...query, ...location.query } };
};
/**
 * `router.replace`, but keep the current query values. (Merge if adding more)
 */
const replaceAndKeepQuery = async (location) => {
    const router = getRouter();
    await router.replace(keepQuery(location));
};
/**
 * `router.push`, but keep the current query values. (Merge if adding more)
 */
const pushAndKeepQuery = async (location) => {
    const router = getRouter();
    await router.push(keepQuery(location));
};
/**
 * Given a new location, merge in the current params values.
 */
const keepParams = (location) => {
    const route = getRoute();
    const { params } = route.value;
    return { ...location, params: { ...params, ...location.params } };
};
/**
 * `router.replace`, but keep the current params values. (Merge if adding more)
 */
const replaceAndKeepParams = async (location) => {
    const router = getRouter();
    await router.replace(keepParams(location));
};
/**
 * `router.push`, but keep the current params values. (Merge if adding more)
 */
const pushAndKeepParams = async (location) => {
    const router = getRouter();
    await router.push(keepParams(location));
};
/**
 * `router.replace`, but keep the current query and params values. (Merge if adding more)
 */
const replaceAndKeepQueryAndParams = async (location) => {
    const router = getRouter();
    await router.replace(keepParams(keepQuery(location)));
};
/**
 * `router.push`, but keep the current query and params values. (Merge if adding more)
 */
const pushAndKeepQueryAndParams = async (location) => {
    const router = getRouter();
    await router.push(keepParams(keepQuery(location)));
};
const rawLocationToLocation = (rawLocation) => {
    if (typeof rawLocation === 'string')
        return { path: rawLocation };
    return rawLocation;
};
/**
 * @deprecated - use `debug` feature flag instead
 * Given a location, add `?debug=1`
 */
const addDebugMode = (location) => {
    const route = getRoute();
    const { query } = route.value;
    return {
        ...rawLocationToLocation(location),
        query: { ...query, debug: '1' },
    };
};
/**
 * @deprecated - use `debug` feature flag instead
 * Given a location, remove `?debug=<any>`
 */
const removeDebugMode = (location) => {
    const route = getRoute();
    const { query } = route.value;
    const queryWithoutDebug = update(query, { $unset: ['debug'] });
    return {
        ...rawLocationToLocation(location),
        query: queryWithoutDebug,
    };
};
/**
 * Convert vue-router type `Route` to `CurrentLocation` (The `Location` type
 * specific to the current app)
 */
const routeToLocation = (route) => {
    var _a;
    if (!route)
        return undefined;
    return {
        ...route,
        name: (_a = route.name) !== null && _a !== void 0 ? _a : undefined,
    };
};
/**
 * When token is present in a URL, token auth is always used. In some cases,
 * such as when generating a login redirect query string, we want to strip the
 * token from the URL.
 *
 * `/resetPassword?token=123&other=456#hash` → `/resetPassword?other=456#hash`
 */
const stripToken = (path) => {
    const redirectUrl = new URL(path, window.location.origin);
    redirectUrl.searchParams.delete('token');
    return redirectUrl.pathname + redirectUrl.search + redirectUrl.hash;
};

export { addDebugMode, buildMaybePartnerRoute, keepParams, keepQuery, pushAndKeepParams, pushAndKeepQuery, pushAndKeepQueryAndParams, rawLocationToLocation, removeDebugMode, replaceAndKeepParams, replaceAndKeepQuery, replaceAndKeepQueryAndParams, routeToLocation, stripToken, updateQuerySilently };
